import { toast } from '@approvalmax/ui/src/components';
import { hooks } from '@approvalmax/utils';
import { selectors } from 'modules/common';
import { domain, State } from 'modules/data';
import { IntegrationSuccessRedirectPage } from 'modules/integration';
import {
    connectToIntegration,
    showDearIntegrationPopup,
    showNetSuiteIntegrationPopup,
} from 'modules/integration/actions';
import { useSelector } from 'modules/react-redux';
import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { amplitudeService } from 'services/amplitude';

import { useOrganisationInfoForm } from '../OrganisationInfo/OrganisationInfo.hooks';
import { OrganisationInfoFormData } from '../OrganisationInfo/OrganisationInfo.types';
import { messages } from './IntegrationSection.messages';

export const useIntegrationSection = (company: selectors.types.ExpandedCompany) => {
    const dispatch = useDispatch();

    const integration = useSelector((state: State) =>
        selectors.integration.getIntegrationByCompanyId(state, company.id)
    );
    const changeIntegrationCommand = useSelector((state: State) =>
        selectors.integration.getChangeIntegrationCommand(state, company.id)
    );

    const integrationType = company.integration && company.integration.integrationType;
    const integrationState = selectors.integration.getExplainedIntegrationState(company.integration);
    const showAllIntegrations = integrationState.neverConnectedBefore && !integrationState.isConnectingFinalizing;

    const prevIntegrationState = hooks.usePrevious(integrationState);

    const hasJustConnected =
        prevIntegrationState &&
        prevIntegrationState.isConnectingFinalizing &&
        integrationState.isConnected &&
        company.integration;

    const hasJustConnectedForTheFirstTime =
        prevIntegrationState && prevIntegrationState.isConnectingFinalizing && integrationState.isConnectedFirstTime;

    const { handleSubmit, formState } = useFormContext<OrganisationInfoFormData>();
    const { saveOrganisation } = useOrganisationInfoForm(company);

    const { isDirty } = formState;

    const onConnect = useCallback(
        async (integrationType: domain.IntegrationType) => {
            if (isDirty) {
                await handleSubmit(saveOrganisation)();
            }

            amplitudeService.sendData('org settings: initiate connection', {
                'connection type':
                    integrationType !== domain.IntegrationType.None
                        ? integrationType.toLocaleLowerCase()
                        : 'standalone',
            });

            if (integrationType === domain.IntegrationType.NetSuite) {
                dispatch(showNetSuiteIntegrationPopup(company.id, IntegrationSuccessRedirectPage.Company));

                return;
            }

            if (integrationType === domain.IntegrationType.Dear) {
                dispatch(showDearIntegrationPopup(company.id, IntegrationSuccessRedirectPage.Company));

                return;
            }

            dispatch(
                connectToIntegration({
                    integrationType,
                    companyId: company.id,
                    finalRedirectToPage: 'company',
                    showGlobalProgress: false,
                })
            );
        },
        [isDirty, dispatch, company.id, handleSubmit, saveOrganisation]
    );

    useEffect(() => {
        if (hasJustConnected) {
            toast.success(
                messages.connectedNotificationMessage({
                    integrationName: selectors.integration.getIntegrationTypeName(integrationType!),
                })
            );
        }
    }, [dispatch, hasJustConnected, hasJustConnectedForTheFirstTime, integrationType]);

    return {
        integration,
        integrationType,
        integrationState,
        showAllIntegrations,
        isDisabled: changeIntegrationCommand.disabled,
        onConnect,
    };
};
